import React, { useState, useEffect, useCallback, useRef } from 'react';
import Icon from '../../common/Icon/Icon';
import { adminFetch, ago, clock, until, shortDate, Dot } from './adminUtil';

/**
 * The card catalog, one panel per game: how big it is, the three nightly
 * jobs that keep it fresh (when each runs next, what it did last time,
 * a Run now button), and a live console for whatever is running.
 *
 * Jobs are a fixed list on the server (helpers/jobs.js), one at a time,
 * and a scheduled run and a button press never overlap.
 */

// What the pipeline wrote, in words
function lastResult(job) {
    if (!job.lastAt) return 'Has never run';
    const d = job.lastDetail || '';
    let m;
    if (job.name === 'prices' && (m = d.match(/updated (\d+), unchanged (\d+)/))) {
        const changed = Number(m[1]), total = Number(m[1]) + Number(m[2]);
        return `Changed ${changed.toLocaleString()} of ${total.toLocaleString()} prices`;
    }
    if (job.name === 'repair' && (m = d.match(/restored (\d+), failed (\d+)(?: on (\S+))?/))) {
        const failed = Number(m[2]);
        return `Restored ${m[1]} image${m[1] === '1' ? '' : 's'}${failed ? `, ${failed} can't be downloaded` : ''}${m[3] ? ` on ${m[3]}` : ''}`;
    }
    if (job.name === 'sync') return 'Checked for new sets';
    return d || 'Finished';
}

// One live connection to the job runner: what's running, and its output
function useJobStream(onFinished) {
    const [state, setState] = useState(null);   // { jobs, current, last }
    const [lines, setLines] = useState([]);
    const [lost, setLost] = useState(false);
    const finishedRef = useRef(onFinished);
    finishedRef.current = onFinished;

    useEffect(() => {
        const es = new EventSource('/api/admin/jobs/stream', { withCredentials: true });
        es.addEventListener('state', e => {
            const s = JSON.parse(e.data);
            setState(s);
            setLines(s.current ? s.current.lines : (s.last ? s.last.lines : []));
            setLost(false);
        });
        es.addEventListener('started', e => {
            const job = JSON.parse(e.data);
            setState(s => ({ ...s, current: job }));
            setLines(job.lines || []);
        });
        es.addEventListener('line', e => setLines(l => [...l.slice(-1999), JSON.parse(e.data)]));
        es.addEventListener('finished', e => {
            const job = JSON.parse(e.data);
            setState(s => ({ ...s, current: null, last: job }));
            finishedRef.current?.();
        });
        es.onerror = () => setLost(true);
        return () => es.close();
    }, []);

    return { state, lines, lost };
}

function Console({ host, stream }) {
    const { state, lines, lost } = stream;
    const [msg, setMsg] = useState(null);
    const logRef = useRef(null);

    useEffect(() => {
        if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
    }, [lines]);

    const cancel = async () => {
        try { await adminFetch('/api/admin/jobs-cancel', { method: 'POST' }); }
        catch (e) { setMsg(e.message); }
    };

    const current = state?.current;
    const last = state?.last;
    const title = current
        ? `${current.label}, running since ${clock(current.startedAt)}`
        : last
            ? `${last.label} ${last.exitCode === 0 ? 'finished' : `failed (exit ${last.exitCode})`} ${ago(last.finishedAt)}`
            : `Nothing has run from this page since ${host} started`;

    return (
        <section className={`adm-console${current ? ' running' : ''}${last && !current && last.exitCode !== 0 ? ' failed' : ''}`}>
            <div className="adm-console-bar">
                <Icon name="terminal" size={14} />
                <span>{title}</span>
                {current && (
                    <button type="button" className="adm-btn danger small" onClick={cancel}>Stop</button>
                )}
            </div>
            <pre ref={logRef}>
                {lines.length ? lines.join('\n') : 'Output from a run shows up here, live.'}
                {current && <span className="adm-cursor" />}
            </pre>
            {lost && <p className="adm-msg">Live output lost its connection; it reconnects by itself.</p>}
            {msg && <p className="adm-msg">{msg}</p>}
        </section>
    );
}

function AdminCatalog({ onChanged }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [recounting, setRecounting] = useState(false);
    const [msg, setMsg] = useState(null);

    const load = useCallback(async (fresh = false) => {
        if (fresh) setRecounting(true);
        try {
            setData(await adminFetch(`/api/admin/data-status${fresh ? '?fresh=1' : ''}`));
            setErr(null);
        } catch (e) { setErr(e.message); }
        finally { setRecounting(false); }
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(load, 60000);
        return () => clearInterval(id);
    }, [load]);

    const stream = useJobStream(() => { load(); onChanged(); });
    const running = stream.state?.current?.name || null;

    const run = async (name) => {
        setMsg(null);
        try { await adminFetch(`/api/admin/jobs/${name}`, { method: 'POST' }); }
        catch (e) { setMsg(e.message); }
    };

    if (err && !data) return <div className="adm-banner bad"><Icon name="alert" size={18} /> {err}</div>;
    if (!data) return <p className="adm-loading">Counting the catalog&hellip;</p>;

    const pricedPct = Math.round(data.pricedCards / Math.max(1, data.cards) * 100);

    return (
        <div className="adm-catalog">
            <section className="adm-game mtg">
                <header className="adm-game-head">
                    <div>
                        <span className="adm-game-name">Magic: The Gathering</span>
                        <strong className="adm-game-count">{data.cards.toLocaleString()} <small>cards</small></strong>
                    </div>
                    <p>
                        {data.sets} sets
                        {data.newestSet && <> · newest <strong>{data.newestSet.setName || data.newestSet.setCode}</strong></>}
                        {' '}· {pricedPct}% have a price
                    </p>
                </header>

                <p className="adm-images">
                    <Dot state={data.missingImages > 0 ? 'idle' : 'ok'} />
                    {data.missingImages > 0
                        ? <>{data.missingImages.toLocaleString()} images aren't on {data.host}'s disk yet. They download the first time someone views them; Image repair fetches the rest.</>
                        : <>Every card image is on {data.host}'s disk.</>}
                    <button type="button" className="adm-link" disabled={recounting} onClick={() => load(true)}>
                        {recounting ? 'Counting…' : `Counted ${ago(data.missingImagesAt)}. Recount`}
                    </button>
                </p>

                <h3 className="adm-h3">Nightly schedule</h3>
                <ol className="adm-stations">
                    {data.jobs.map(j => (
                        <li key={j.name} className={`adm-station ${j.state}${running === j.name ? ' running' : ''}`}>
                            <span className="adm-station-time">
                                <Icon name="clock" size={13} /> {clock(j.nextAt)}
                                <small>{j.every === 'daily' ? 'every night' : j.every}</small>
                            </span>
                            <strong>{j.label}</strong>
                            <span className="adm-station-last">
                                <Dot state={running === j.name ? 'run' : j.state} pulse={running === j.name} />
                                {running === j.name ? 'Running now' : lastResult(j)}
                            </span>
                            <span className="adm-station-when">
                                {j.lastAt ? `Last ran ${ago(j.lastAt)} · next ${until(j.nextAt)}` : `First run ${until(j.nextAt)}`}
                            </span>
                            {j.note && running !== j.name && (
                                <span className="adm-station-note">
                                    {/^Changed no prices/.test(j.note) ? 'Probably reused an old price file. Run it again.' : j.note}
                                </span>
                            )}
                            <button type="button" className="adm-btn primary small" disabled={!!running} onClick={() => run(j.name)}>
                                <Icon name="play" size={11} /> Run now
                            </button>
                        </li>
                    ))}
                </ol>
                <p className="adm-muted adm-schedule-note">
                    Times are yours; the live server runs them from cron. Last new-set check {data.meta?.last_sync ? shortDate(data.meta.last_sync.at) : 'never'}.
                    <button type="button" className="adm-link" disabled={!!running} onClick={() => run('status')}>
                        List missing sets (changes nothing)
                    </button>
                </p>
                {msg && <p className="adm-msg">{msg}</p>}
            </section>

            <Console host={data.host} stream={stream} />

            <section className="adm-game pokemon off">
                <header className="adm-game-head">
                    <div>
                        <span className="adm-game-name">Pokémon</span>
                        <strong className="adm-game-count">{data.pokemon.cards.toLocaleString()} <small>cards</small></strong>
                    </div>
                    <p>{data.pokemon.ready ? 'Catalog loaded.' : 'Not added yet. Its database is ready and empty; its nightly jobs will appear here.'}</p>
                </header>
            </section>
        </div>
    );
}

export default AdminCatalog;
